import * as recallApi from '@otter-ai/recall-client';
import { getCampaignProperties } from '@otter-ai/telemetry';
import { useMutation } from '@tanstack/react-query';
import { CheckEmailApiResponse } from '#mutation';
import { AB_TESTING_GROUP, CampaignParams, UserExperimentType } from '#types';
import {
  getExperiment,
  getFromLocalStorage,
  LOCAL_STORAGE_KEYS,
  removeFromLocalStorage,
} from '#utils';

function utmIsValidPaidMedia(utmParams: CampaignParams) {
  const utmSource = utmParams.utm_source;
  return utmSource?.endsWith('_ads') || utmSource === 'affiliate';
}

export const useCheckPaidMediaOnboardingEligibility = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const {
    mutate: checkPaidMediaOnboardingEligibility,
    isPending: isCheckingPaidMediaOnboardingEligibility,
  } = useMutation({
    mutationFn: async ({
      utmSource,
      utmMedium,
      hasPendingInvite,
      isEligible,
    }: {
      utmSource?: string;
      utmMedium?: string;
      hasPendingInvite: boolean;
      isEligible?: boolean;
    }) => {
      const body = {
        utm_source: utmSource,
        utm_medium: utmMedium,
        has_pending_invite: hasPendingInvite,
        is_eligible: isEligible,
      };

      const response = await recallApi.checkPaidMediaOnboardingEligibility({
        body,
        throwOnError: true,
      });

      return response.data;
    },
    onSuccess: async () => {
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      if (onError) onError(error);
    },
  });

  const isPaidMediaSignupFlow = (
    experiments: UserExperimentType[],
    checkEmailResponse: CheckEmailApiResponse | null,
  ) => {
    const hasPendingInvite =
      !!checkEmailResponse?.data?.workspace.is_pending_member;

    const isStoreUtmParamsEnabled = getExperiment(
      'store_utm_params',
      AB_TESTING_GROUP.TREATMENT,
      experiments,
    );

    const utmParams = getCampaignProperties();

    // This flag is set in storage by marketing pages
    const isCampaignPaidMedia = !!getFromLocalStorage(
      LOCAL_STORAGE_KEYS.CAMPAIGN_PAID_MEDIA_SIGNUP_FLOW,
      true,
    );

    const isEligibleForPaidMediaSignupFlow = utmParams
      ? !hasPendingInvite &&
        (utmIsValidPaidMedia(utmParams) || isCampaignPaidMedia)
      : false;

    // Remove flag after the user signs up
    if (isCampaignPaidMedia) {
      removeFromLocalStorage(
        LOCAL_STORAGE_KEYS.CAMPAIGN_PAID_MEDIA_SIGNUP_FLOW,
        true,
      );
    }

    if (
      utmParams?.utm_source &&
      utmParams.utm_medium &&
      isStoreUtmParamsEnabled
    ) {
      // stores utm params in backend
      checkPaidMediaOnboardingEligibility({
        utmSource: utmParams.utm_source,
        utmMedium: utmParams.utm_medium,
        hasPendingInvite,
        isEligible: isEligibleForPaidMediaSignupFlow,
      });
    }

    return isEligibleForPaidMediaSignupFlow;
  };

  return {
    checkPaidMediaOnboardingEligibility,
    isCheckingPaidMediaOnboardingEligibility,
    isPaidMediaSignupFlow,
  };
};
